import type { MessageBundle } from './format-message';
import { setOverlayBundle } from './I18nProvider';
import { PSEUDO_LOCALE, type Locale, type ShippedLocale } from './locales';

/*
 * W2 (spec §4.4, ADR-0502 §9): terminology overlays. A pack or app may re-word generic keys for its country or domain
 * ("HS code" → "GTİP" when `tr.customs` is installed, "Warentarifnummer" under a DE pack), so the core catalogue never
 * hard-codes a country term. Each overlay is `overlays/<pack or app id>/<locale>.json`: a flat bundle of keys that
 * already exist in the base catalogue. It is loaded only when the owner is enabled and only for the languages in use;
 * a language without a file keeps the base text.
 *
 * The app decides which overlays are active from the tenant's enablement (`I18nOverlayBridge` in the web app); this
 * module knows only ids and files.
 */

type OverlayLoaders = Partial<Record<ShippedLocale, () => Promise<MessageBundle>>>;

/** Every overlay the product ships, by owner id (the pack or app id of the enablement contract). */
const overlayLoaders: Record<string, OverlayLoaders> = {
  'tr.customs': {
    en: () => import('./overlays/tr.customs/en.json').then((m) => m.default as MessageBundle),
    tr: () => import('./overlays/tr.customs/tr.json').then((m) => m.default as MessageBundle),
  },
};

/** The owner ids that ship an overlay. */
export const TERMINOLOGY_OVERLAYS: readonly string[] = Object.keys(overlayLoaders);

const cache = new Map<string, Promise<MessageBundle>>();

function loadOverlay(id: string, locale: Locale): Promise<MessageBundle> {
  const load = locale === PSEUDO_LOCALE ? undefined : overlayLoaders[id]?.[locale];
  if (!load) return Promise.resolve({});
  const key = `${id}/${locale}`;
  let pending = cache.get(key);
  if (!pending) {
    pending = load().catch(() => {
      cache.delete(key);
      return {};
    });
    cache.set(key, pending);
  }
  return pending;
}

/** The languages an overlay was last applied to, so switching an overlay off clears them all. */
const appliedLocales = new Set<Locale>();
let generation = 0;

/**
 * Lays the overlays of `ids` (in order; a later one wins on a shared key) over the catalogues of `locales`, and
 * removes every overlay no longer named. Calls may overlap (a language switch while an app is installed): only the
 * last one's result is applied.
 */
export async function applyTerminologyOverlays(ids: readonly string[], locales: readonly Locale[]): Promise<void> {
  const run = ++generation;
  const active = ids.filter((id) => id in overlayLoaders);
  const targets = [...new Set<Locale>([...locales, ...appliedLocales])];
  const bundles = await Promise.all(targets.map(async (locale) => [locale, Object.assign({}, ...(await Promise.all(active.map((id) => loadOverlay(id, locale))))) as MessageBundle] as const));
  if (run !== generation) return;
  for (const [locale, bundle] of bundles) {
    // Nothing laid over this language before and nothing now: no re-render.
    if (!Object.keys(bundle).length && !appliedLocales.has(locale)) continue;
    setOverlayBundle(locale, bundle);
    if (Object.keys(bundle).length) appliedLocales.add(locale);
    else appliedLocales.delete(locale);
  }
}
